import { createTheme } from '@mui/material/styles';

// Расширяем типы палитры для градиентов
declare module '@mui/material/styles' {
  interface Palette {
    gradient: {
      primary: string;
      secondary: string;
      success: string;
      error: string;
      info: string;
    };
  }
  interface PaletteOptions {
    gradient?: {
      primary?: string;
      secondary?: string;
      success?: string;
      error?: string;
      info?: string;
    };
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#6366F1', // Indigo-500 (современный синий)
      light: '#818CF8', // Indigo-400
      dark: '#4F46E5', // Indigo-600
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#F59E0B', // Amber-500 (современный оранжевый)
      light: '#FBBF24', // Amber-400
      dark: '#D97706', // Amber-600
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#10B981', // Emerald-500
      light: '#34D399', // Emerald-400
      dark: '#059669', // Emerald-600
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#EF4444', // Red-500
      light: '#F87171', // Red-400
      dark: '#DC2626', // Red-600
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#F59E0B', // Amber-500
      light: '#FBBF24', // Amber-400
      dark: '#D97706', // Amber-600
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#3B82F6', // Blue-500
      light: '#60A5FA', // Blue-400
      dark: '#2563EB', // Blue-600
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F9FAFB', // Gray-50 (более светлый)
      paper: '#FFFFFF',
    },
    gradient: {
      primary: 'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)',
      secondary: 'linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)',
      success: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
      error: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
      info: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
      fontSize: '2.5rem',
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      '@media (max-width:600px)': {
        fontSize: '1.25rem',
      },
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      '@media (max-width:600px)': {
        fontSize: '1.125rem',
      },
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      '@media (max-width:600px)': {
        fontSize: '0.875rem',
      },
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 16, // Увеличенные скругления для современного вида
  },
  shadows: [
    'none',
    '0 2px 8px rgba(0,0,0,0.04)',
    '0 4px 12px rgba(0,0,0,0.06)',
    '0 6px 16px rgba(0,0,0,0.08)',
    '0 8px 24px rgba(0,0,0,0.10)',
    '0 12px 32px rgba(0,0,0,0.12)',
    '0 16px 40px rgba(0,0,0,0.14)',
    '0 20px 48px rgba(0,0,0,0.16)',
    '0 24px 56px rgba(0,0,0,0.18)',
    '0 8px 32px rgba(99, 102, 241, 0.15)', // Primary shadow
    ...Array(15).fill('0 4px 24px rgba(0,0,0,0.08)'),
  ] as any,
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 600,
          textTransform: 'none',
          padding: '10px 24px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        contained: {
          textTransform: 'none',
          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(99, 102, 241, 0.35)',
            transform: 'translateY(-2px)',
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #5568D3 0%, #63408E 100%)',
          },
        },
        outlined: {
          textTransform: 'none',
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
            transform: 'translateY(-2px)',
          },
        },
        text: {
          textTransform: 'none',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          minWidth: 72,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 10,
          padding: '4px 8px',
        },
        filled: {
          background: 'linear-gradient(135deg, #667EEA 0%, #764BA2 100%)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
        },
        elevation1: {
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
        },
        elevation2: {
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        },
        elevation3: {
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.10)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.1)',
            },
            '&.Mui-focused': {
              boxShadow: '0 4px 16px rgba(99, 102, 241, 0.2)',
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 24px 64px rgba(0, 0, 0, 0.15)',
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
        },
      },
    },
  },
});

export default theme;